import { getAuth } from "firebase/auth";
import { logger } from '../utils/logger';
import { authAxios } from '../utils/apiConfig';

const aiAxios = authAxios;

// Get AI-powered keyword suggestions based on business vertical
export async function getAIKeywordSuggestions(vertical, count = 10) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await aiAxios.post('/ai/keyword-suggestions', null, {
      params: {
        user: user.email,
        vertical: vertical,
        count: count
      }
    });

    return response.data;
  } catch (error) {
    logger.error("Error in getAIKeywordSuggestions:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.detail || error.response.data.message || 'Server error');
    }
    throw error;
  }
}

// Get AI-powered competitor analysis keywords
export async function getAICompetitorKeywords(domain, vertical, count = 8) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await aiAxios.post('/ai/competitor-keywords', null, {
      params: {
        user: user.email,
        domain: domain,
        vertical: vertical,
        count: count
      }
    });

    return response.data;
  } catch (error) {
    logger.error("Error in getAICompetitorKeywords:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.detail || error.response.data.message || 'Server error');
    }
    throw error;
  }
}

/**
 * Generate an AI-powered 3-part "Immediate Action" plan for a business:
 * Findability (a public KPI, never sentiment), Ads (based on ad volume vs.
 * competitors), and Internal (based on the biggest SWOT weakness).
 *
 * @param {Object} params
 * @param {string} params.businessName - Target business name
 * @param {string} [params.mapsUri] - Google Maps URI for richer context
 * @param {Object} params.ranks - Ranks per category
 *   { growth, total_reviews, traffic, sentiment, seo }
 * @param {Object} params.totals - Total competitors per category
 *   { growth, total_reviews, traffic, sentiment, seo }
 * @param {Object} [params.adsContext] - Ad-running context for the business
 *   {
 *     ad_count, rank, total_businesses,
 *     advertiser_rank, total_advertisers,
 *     avg_ads_market, avg_ads_advertisers
 *   }
 * @param {string} [params.swotWeaknesses] - Plain-text Weaknesses section
 *   from the business's SWOT analysis. Used to drive the Internal action.
 * @returns {Promise<Object>} response.data.data containing
 *   { findability: {...}, ads: {...}, internal: {...} }
 */
export async function getImmediateAction({
  businessName,
  mapsUri,
  ranks,
  totals,
  adsContext,
  swotWeaknesses,
}) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await aiAxios.post(
      '/ai/generate-immediate-action',
      {
        business_name: businessName,
        maps_uri: mapsUri || null,
        ranks,
        totals,
        ads_context: adsContext || null,
        swot_weaknesses: swotWeaknesses || null,
      },
      {
        params: { user: user.email },
      }
    );

    return response.data;
  } catch (error) {
    logger.error('Error in getImmediateAction:', error);
    if (error.response) {
      logger.error('Server response:', error.response.data);
      throw new Error(
        error.response.data.detail ||
          error.response.data.message ||
          'Server error'
      );
    }
    throw error;
  }
}

/**
 * Generate EXACTLY 3 prioritized to-do bullets for a single business,
 * rendered next to the Localytics Score rankings panel.
 *
 * Backed by POST /ai/generate-todo-bullets. The bullets follow a fixed
 * format: **[Action]** — [data point]. [next step]. The AI prioritizes
 *   1) fastest-moving negative trend,
 *   2) structural gap on the highest-leverage metric,
 *   3) defensive play on the strongest metric,
 * and is forbidden from recommending categories where the business is
 * already top-3.
 *
 * @param {Object} params
 * @param {string} params.businessName
 * @param {string} [params.mapsUri]
 * @param {Object} params.rankings - { growth, total_reviews, traffic, sentiment, seo }
 * @param {Object} params.totals - same keys as rankings
 * @param {Object} [params.rawMetrics] - {
 *     total_reviews, star_rating, monthly_review_velocity,
 *     velocity_change_30d, sentiment_score, sentiment_trend,
 *     organic_traffic, traffic_trend, seo_score,
 *     competitor_avg_star_rating
 *   }
 * @param {string[]} [params.negativeThemes] - top recurring 1-star themes
 * @param {Object} [params.adData] - {
 *     running_ads, channels, spend, ad_count, rank,
 *     total_businesses, avg_ads_market
 *   }
 * @returns {Promise<{ success: boolean, data: { todos: Array<{
 *   action: string, data_point: string, next_step: string, bullet: string
 * }> } }>}
 */
export async function getTodoBullets({
  businessName,
  mapsUri,
  rankings,
  totals,
  rawMetrics,
  negativeThemes,
  adData,
}) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await aiAxios.post(
      '/ai/generate-todo-bullets',
      {
        business_name: businessName,
        maps_uri: mapsUri || null,
        rankings: rankings || {},
        totals: totals || {},
        raw_metrics: rawMetrics || {},
        negative_themes: negativeThemes || null,
        ad_data: adData || null,
      },
      {
        params: { user: user.email },
      }
    );

    return response.data;
  } catch (error) {
    logger.error('Error in getTodoBullets:', error);
    if (error.response) {
      logger.error('Server response:', error.response.data);
      throw new Error(
        error.response.data.detail ||
          error.response.data.message ||
          'Server error'
      );
    }
    throw error;
  }
}

/**
 * Generate the "Keep More Customers" retention plays for a single
 * business, rendered next to the "Get More Customers" panel below the
 * Localytics Score breakdown.
 *
 * Backed by POST /ai/generate-retention-plays. Returns two grouped
 * lists derived from the business's own reviews (a lightweight SWOT):
 *   - keep:    what to KEEP doing, from the themes 5-star reviewers praise
 *   - reverse: what to REVERSE / fix, from recurring negative-review themes
 *
 * @param {Object} params
 * @param {string} params.mapsUri - Google Maps URI of the target business.
 * @param {string} [params.businessName]
 * @param {string[]} [params.positiveThemes] - common_positive_themes
 * @param {string[]} [params.negativeThemes] - common_negative_themes
 * @returns {Promise<{ success: boolean, data: {
 *   keep: Array<{action:string, detail:string}>,
 *   reverse: Array<{action:string, detail:string}>
 * } }>}
 */
export async function getRetentionPlays({
  mapsUri,
  businessName,
  positiveThemes,
  negativeThemes,
}) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await aiAxios.post(
      '/ai/generate-retention-plays',
      {
        maps_uri: mapsUri || null,
        business_name: businessName || null,
        positive_themes: positiveThemes || null,
        negative_themes: negativeThemes || null,
      },
      {
        params: { user: user.email },
      }
    );

    return response.data;
  } catch (error) {
    logger.error('Error in getRetentionPlays:', error);
    if (error.response) {
      logger.error('Server response:', error.response.data);
      throw new Error(
        error.response.data.detail ||
          error.response.data.message ||
          'Server error'
      );
    }
    throw error;
  }
}

/**
 * Generate (or fetch cached) UNIQUE business-specific SWOT insights for a
 * single business. Backed by POST /ai/generate-business-insights, which
 * stores results in the business's `versus_insights` field on first call
 * and returns the cached blob on subsequent calls unless forceRegenerate
 * is true.
 *
 * @param {Object} params
 * @param {string} params.mapsUri - Google Maps URI of the target business.
 * @param {boolean} [params.forceRegenerate=false] - Bypass cache.
 * @returns {Promise<{ versus_insights: string|null, cached: boolean }>}
 *   Normalized response. Throws on server / network errors.
 */
export async function generateBusinessSwotInsights({
  mapsUri,
  forceRegenerate = false,
}) {
  if (!mapsUri) {
    throw new Error('mapsUri is required to generate SWOT insights');
  }

  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await aiAxios.post(
      '/ai/generate-business-insights',
      {
        maps_uri: mapsUri,
        force_regenerate: !!forceRegenerate,
      },
      {
        params: { user: user.email },
      }
    );

    const payload = response?.data?.data || {};
    return {
      versus_insights: payload.versus_insights || null,
      cached: !!payload.cached,
    };
  } catch (error) {
    logger.error('Error in generateBusinessSwotInsights:', error);
    if (error.response) {
      logger.error('Server response:', error.response.data);
      throw new Error(
        error.response.data.detail ||
          error.response.data.message ||
          'Server error'
      );
    }
    throw error;
  }
}

/**
 * Fetch the actual high- and low-rated review excerpts that fed the
 * AI-generated SWOT analysis for a single business. Mirrors the same
 * `review_excerpts` payload Gemini receives, so the UI can present them
 * as evidence behind the SWOT bullets.
 *
 * @param {Object} params
 * @param {string} params.mapsUri - Google Maps URI of the target business.
 * @param {number} [params.highLimit=8] - Max high-rated reviews to return.
 * @param {number} [params.lowLimit=8] - Max low-rated reviews to return.
 * @returns {Promise<{
 *   business_name: string|null,
 *   maps_uri: string,
 *   rating_avg: number|null,
 *   rating_count: number|null,
 *   total_excerpts: number,
 *   high_reviews: Array<{text:string,rating:number,reviewer_name?:string,review_url?:string,time?:string}>,
 *   low_reviews: Array<{text:string,rating:number,reviewer_name?:string,review_url?:string,time?:string}>,
 *   has_versus_insights: boolean,
 * }>} Normalized SWOT evidence payload. Throws on server / network errors.
 */
export async function getSwotEvidence({ mapsUri, highLimit = 8, lowLimit = 8 }) {
  if (!mapsUri) {
    throw new Error('mapsUri is required to load SWOT evidence');
  }

  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const response = await aiAxios.get('/ai/swot-evidence/', {
      params: {
        maps_uri: mapsUri,
        user: user.email,
        high_limit: highLimit,
        low_limit: lowLimit,
      },
    });

    return response?.data?.data || {
      business_name: null,
      maps_uri: mapsUri,
      rating_avg: null,
      rating_count: null,
      total_excerpts: 0,
      high_reviews: [],
      low_reviews: [],
      has_versus_insights: false,
    };
  } catch (error) {
    logger.error('Error in getSwotEvidence:', error);
    if (error.response) {
      logger.error('Server response:', error.response.data);
      throw new Error(
        error.response.data.detail ||
          error.response.data.message ||
          'Server error'
      );
    }
    throw error;
  }
}

// Detect business vertical from review data
export function detectVerticalFromData(reviewsData) {
  if (!reviewsData || reviewsData.length === 0) return 'general';
  
  // Extract primary types and categorize
  const types = reviewsData
    .map(business => business.primary_type || business.category)
    .filter(type => type)
    .map(type => type.toLowerCase());
  
  // Common vertical mappings
  const verticalMappings = {
    'restaurant': ['restaurant', 'food', 'cafe', 'bar', 'bakery', 'meal_takeaway'],
    'healthcare': ['hospital', 'doctor', 'dentist', 'medical', 'health', 'pharmacy'],
    'retail': ['store', 'shop', 'clothing', 'shopping', 'electronics', 'book_store'],
    'automotive': ['car_dealer', 'car_repair', 'gas_station', 'automotive'],
    'professional_services': ['lawyer', 'accountant', 'consultant', 'real_estate', 'insurance'],
    'beauty': ['beauty_salon', 'hair_care', 'spa', 'nail_salon'],
    'fitness': ['gym', 'fitness', 'yoga', 'sports'],
    'education': ['school', 'university', 'training'],
    'hospitality': ['lodging', 'hotel', 'travel_agency'],
    'home_services': ['plumber', 'electrician', 'contractor', 'cleaning']
  };
  
  // Count matches for each vertical
  const verticalCounts = {};
  for (const [vertical, keywords] of Object.entries(verticalMappings)) {
    verticalCounts[vertical] = types.filter(type => 
      keywords.some(keyword => type.includes(keyword))
    ).length;
  }
  
  // Return the vertical with the highest count
  const detectedVertical = Object.keys(verticalCounts).reduce((a, b) => 
    verticalCounts[a] > verticalCounts[b] ? a : b
  );
  
  return verticalCounts[detectedVertical] > 0 ? detectedVertical : 'general';
}

// Export all functions as default object
const aiKeywordService = {
  getAIKeywordSuggestions,
  getAICompetitorKeywords,
  getImmediateAction,
  getTodoBullets,
  getRetentionPlays,
  generateBusinessSwotInsights,
  getSwotEvidence,
  detectVerticalFromData
};

export default aiKeywordService;